import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** When set, user must type this exact string to enable Confirm. Used for irreversible actions (e.g. "DELETE"). */
  typedConfirmation?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Modal confirmation dialog. Used for destructive admin actions
 * (bulk-delete, revoke code, reset preferences).
 *
 * If `typedConfirmation` is set, the Confirm button stays disabled until
 * the user types the exact string — guards against muscle-memory clicks
 * on irreversible operations.
 */
export const ConfirmDialog: React.FC<Props> = ({
  open,
  title,
  description,
  confirmLabel = 'Bevestigen',
  cancelLabel = 'Annuleren',
  destructive = false,
  typedConfirmation,
  onConfirm,
  onCancel,
}) => {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setTyped(''); setBusy(false); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onCancel]);

  if (!open) return null;

  const matched = !typedConfirmation || typed === typedConfirmation;

  const run = async () => {
    if (!matched || busy) return;
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };

  return (
    <div data-testid="confirm-dialog" className="fixed inset-0 z-[700] flex items-center justify-center px-5 bg-ink/55 dark:bg-black/75 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="w-full max-w-md bg-white dark:bg-ink rounded-2xl border border-black/8 dark:border-white/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.4)] overflow-hidden">
        <div className="p-5 flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full grid place-items-center flex-shrink-0 ${destructive ? 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400' : 'bg-accent/15 text-accent'}`}>
            <AlertTriangle size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold tracking-[-0.01em] text-ink dark:text-white">{title}</h3>
            {description && <div className="text-sm text-ink/65 dark:text-white/65 mt-1 leading-relaxed">{description}</div>}
            {typedConfirmation && (
              <div className="mt-3">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-ink/55 dark:text-white/55 mb-1">
                  Typ <span className="font-mono text-red-600 dark:text-red-400">{typedConfirmation}</span> om te bevestigen
                </label>
                <input
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/15 bg-white dark:bg-ink/60 dark:text-white focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 font-mono"
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 bg-black/[0.02] dark:bg-white/[0.03] border-t border-black/8 dark:border-white/10">
          <button
            type="button"
            data-testid="confirm-dialog-cancel"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 rounded-md border border-black/10 dark:border-white/15 hover:bg-black/[0.03] dark:hover:bg-white/5 text-xs font-semibold text-ink/75 dark:text-white/75 transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            data-testid="confirm-dialog-confirm"
            onClick={run}
            disabled={!matched || busy}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              destructive
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-accent hover:bg-accent-hover'
            }`}
          >
            {busy ? 'Bezig…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
